import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { Helmet } from 'react-helmet';

const TermsOfService = () => {
  const navigate = useNavigate();

  const termsSection = [
    {
      title: 'User Agreement',
      content: [
        'By accessing ShaadiNest, you agree to be bound by these Terms of Service and all applicable laws and regulations.',
        'Account creation requires accurate information and verification of identity for both couples and vendors.',
        'Users must be at least 18 years old to create an account and use our services.',
        'Each user is responsible for maintaining the confidentiality of their account credentials.'
      ]
    },
    {
      title: 'Platform Usage',
      content: [
        'ShaadiNest provides a marketplace connecting couples with verified South Asian wedding vendors.',
        'All communications between users must remain respectful and culturally sensitive.',
        'Users may not use the platform for any unlawful or fraudulent activities.',
        'Content shared must be appropriate and relevant to wedding planning services.'
      ]
    },
    {
      title: 'Vendor Responsibilities',
      content: [
        'Vendors must provide accurate information about their services, pricing, and availability.',
        'All portfolio images and service descriptions must be truthful and owned by the vendor.',
        'Vendors are required to maintain cultural sensitivity and understanding of South Asian traditions.',
        'Response times to customer inquiries should be within 24-48 hours during business days.'
      ]
    },
    {
      title: 'Payment Terms',
      content: [
        'Platform fees are clearly disclosed during the booking process.',
        'Refund policies vary by vendor and are outlined in individual service agreements.',
        'Payment processing is secured through industry-standard encryption.',
        'Dispute resolution services are available for booking-related issues.'
      ]
    },
    {
      title: 'Privacy Protection',
      content: [
        'Personal information is protected according to our Privacy Policy.',
        'Wedding details and vendor communications remain confidential.',
        'Users control visibility settings for their profiles and information.',
        'Data is not shared with third parties without explicit consent.'
      ]
    },
    {
      title: 'Dispute Resolution',
      content: [
        'Initial disputes should be resolved directly between couples and vendors.',
        'ShaadiNest provides mediation services for unresolved conflicts.',
        'Arbitration may be required for disputes exceeding specified amounts.',
        'Legal proceedings are subject to California state jurisdiction.'
      ]
    }
  ];

  const quickLinks = [
    { label: 'Privacy Policy', path: '/privacy-policy' },
    { label: 'Contact Support', path: '/contact-us' },
    { label: 'Help Center', path: '/help-center' },
    { label: 'About Us', path: '/about-us' }
  ];

  const handleContactUs = () => {
    navigate('/contact-us');
  };

  const handlePrivacyPolicy = () => {
    navigate('/privacy-policy');
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Terms of Service - ShaadiNest | Legal Terms & Conditions</title>
        <meta 
          name="description" 
          content="Read ShaadiNest's comprehensive Terms of Service covering user agreements, vendor responsibilities, payment terms, and dispute resolution for our wedding marketplace platform." 
        />
      </Helmet>

      <Header />

      <div className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary-50 to-secondary-50 py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="font-heading font-bold text-4xl sm:text-5xl text-text-primary mb-6">
                Terms of Service
              </h1>
              <p className="text-xl text-text-secondary mb-4 max-w-3xl mx-auto">
                Understanding your rights and responsibilities when using ShaadiNest's wedding marketplace platform
              </p>
              <div className="flex items-center justify-center space-x-2 text-text-muted">
                <Icon name="Calendar" size={16} />
                <span className="text-sm">Last updated: July 12, 2025</span>
              </div>
            </div>
          </div>
        </section>

        {/* Table of Contents */}
        <section className="py-12 bg-surface-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-xl shadow-soft p-6">
              <h2 className="font-heading font-semibold text-lg text-text-primary mb-4 flex items-center">
                <Icon name="List" size={20} className="mr-2" />
                Table of Contents
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {termsSection.map((section, index) => (
                  <a
                    key={index}
                    href={`#section-${index}`}
                    className="flex items-center space-x-2 p-2 rounded-lg hover:bg-primary-50 hover:text-primary transition-colors duration-200"
                  >
                    <span className="text-sm font-medium">{index + 1}.</span>
                    <span className="text-sm">{section.title}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Terms Content */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Introduction */}
            <div className="mb-12">
              <div className="bg-white rounded-xl shadow-soft p-8">
                <h2 className="font-heading font-semibold text-2xl text-text-primary mb-4">
                  Welcome to ShaadiNest
                </h2>
                <p className="text-text-secondary mb-4">
                  These Terms of Service ("Terms") govern your use of ShaadiNest's wedding marketplace platform, including our website, mobile applications, and related services. By accessing or using our platform, you agree to these Terms and our Privacy Policy.
                </p>
                <p className="text-text-secondary">
                  ShaadiNest is designed specifically for the South Asian wedding community, connecting couples with culturally-aware vendors who understand the traditions and customs that make your celebration special.
                </p>
              </div>
            </div>

            {/* Terms Sections */}
            <div className="space-y-8">
              {termsSection.map((section, index) => (
                <div key={index} id={`section-${index}`} className="bg-white rounded-xl shadow-soft p-8">
                  <h3 className="font-heading font-semibold text-xl text-text-primary mb-6 flex items-center">
                    <span className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white text-sm font-bold mr-3">
                      {index + 1}
                    </span>
                    {section.title}
                  </h3>
                  <div className="space-y-4">
                    {section.content.map((paragraph, paragraphIndex) => (
                      <div key={paragraphIndex} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-text-secondary leading-relaxed">{paragraph}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Important Notice */}
            <div className="mt-12">
              <div className="bg-warning-50 border border-warning-200 rounded-xl p-6">
                <div className="flex items-start space-x-3">
                  <Icon name="AlertTriangle" size={24} className="text-warning flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-lg text-warning-800 mb-2">
                      Important Legal Notice
                    </h3>
                    <p className="text-warning-700 mb-3">
                      These Terms of Service constitute a legally binding agreement. Please read them carefully and contact us if you have any questions before using our platform.
                    </p>
                    <p className="text-warning-700">
                      We reserve the right to modify these terms at any time. Continued use of the platform after changes constitutes acceptance of the updated terms.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="mt-12">
              <div className="bg-surface-100 rounded-xl p-8">
                <h3 className="font-heading font-semibold text-xl text-text-primary mb-4">
                  Questions About These Terms?
                </h3>
                <p className="text-text-secondary mb-6">
                  If you have any questions about these Terms of Service, please contact our legal team. We're here to help clarify any aspects of our platform policies.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    variant="primary"
                    size="md"
                    iconName="Mail"
                    onClick={handleContactUs}
                  >
                    Contact Legal Team
                  </Button>
                  <Button
                    variant="outline"
                    size="md"
                    iconName="Shield"
                    onClick={handlePrivacyPolicy}
                  >
                    View Privacy Policy
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Links */}
        <section className="py-12 bg-surface-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-xl shadow-soft p-6">
              <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">
                Related Resources
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {quickLinks.map((link, index) => (
                  <button
                    key={index}
                    onClick={() => navigate(link.path)}
                    className="flex items-center space-x-2 p-3 rounded-lg hover:bg-primary-50 hover:text-primary transition-colors duration-200 text-left"
                  >
                    <Icon name="ArrowRight" size={16} />
                    <span className="text-sm font-medium">{link.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default TermsOfService;